<div class="flex flex-col gap-2 mb-5">
  @if (briefingTimeline()) {
    <div class="flex flex-col gap-1">
      <span class="text-sm">
        {{ 'pages.campaignDetails.contents.filters.title' | translate }}
      </span>
      <div class="flex items-center gap-4 pl-1 mb-1 max-md:pl-0">
        <span class="text-xs text-shikenso-purple-base-60 max-md:hidden">
          {{ 'pages.campaignDetails.creatorTimeline.status' | translate }}
        </span>

        <app-campaign-details-deliverables-status-filters
          [loaderCount]="3"
          [loading]="loading()"
          [selectedStatuses]="$any(selectedBriefingStatuses())"
          [statusOptions]="briefingStatusOptions()"
          (selectedStatusesChanged)="onBriefingStatusesChanged($any($event))"
        />
      </div>
    </div>
  } @else if (!noCampaignContextMode()) {
    <p class="text-sm text-shikenso-purple-base-60 mb-3" data-testid="social-content-tiles-description">
      {{ 'pages.campaignDetails.contents.description' | translate }}
    </p>

    <span class="text-sm mb-1">
      {{ 'pages.campaignDetails.contents.filters.title' | translate }}
    </span>

    <div class="flex items-center gap-4 pl-1">
      @if (!showInitialSkeleton()) {
        <app-campaign-details-deliverables-filter-select
          class="w-[252px]"
          [emptyLabel]="matchingEmptyLabel()"
          [loading]="showInitialSkeleton()"
          [options]="matchingTypeOptions()"
          [selectedValues]="selectedMatchingTypes()"
          [showBulkActions]="true"
          [showSearch]="false"
          (selectionChanged)="onMatchingTypesChanged($event)"
        />

        <mat-form-field class="custom-form flex-1 tile-search">
          <app-icon src="/icons/search.svg" matPrefix alt="Search" class="w-4 h-4" />
          <input
            matInput
            [formControl]="searchControl"
            [placeholder]="'pages.campaignDetails.contents.filters.searchPlaceholder' | translate"
          />
        </mat-form-field>
      } @else {
        <ngx-skeleton-loader
          class="h-[30px] flex"
          [theme]="{ height: '30px', width: '252px', 'border-radius': '100vw' }"
        />
        <div class="flex flex-1">
          <ngx-skeleton-loader
            class="h-[30px] w-full"
            [theme]="{ height: '30px', width: '100%', 'border-radius': '100vw' }"
          />
        </div>
      }
    </div>

    <div class="flex flex-col overflow-hidden pl-2 mb-1">
      <div class="flex items-center justify-between">
        <div
          class="overflow-hidden"
          [@expandCollapse]="showInitialSkeleton() || showContentStatusFilter() ? 'expanded' : 'collapsed'"
        >
          <div class="flex items-center gap-6">
            <span class="text-xs text-shikenso-purple-base-60">
              {{ 'pages.campaignDetails.contents.filters.contentStatus' | translate }}
            </span>

            <app-campaign-details-deliverables-status-filters
              [loaderCount]="3"
              [loading]="showInitialSkeleton()"
              [selectedStatuses]="$any(selectedContentStatuses())"
              [statusOptions]="visibleContentStatusOptions()"
              (selectedStatusesChanged)="onContentStatusesChanged($event)"
            />
          </div>
        </div>

        <div class="flex items-center gap-3 text-sm shrink-0 min-h-7">
          <span>{{ 'pages.campaignDetails.contents.filters.onlyWithObservationFindings' | translate }}</span>
          <mat-slide-toggle
            [checked]="onlyWithObservationFindings()"
            [hideIcon]="true"
            (change)="onlyWithObservationFindings.set($event.checked)"
          />
        </div>
      </div>

      @if (filteredCreator() || filteredGroup()) {
        <div class="flex flex-wrap items-center gap-x-8">
          @if (filteredCreator()) {
            <app-filter-badge
              label="components.filterBadge.creator"
              [showAvatar]="true"
              [avatar]="filteredCreator()!.icon"
              [name]="filteredCreator()!.name"
              (removed)="filteredCreatorRemoved.emit()"
            />
          }

          @if (filteredGroup()) {
            <app-filter-badge
              label="components.filterBadge.deliverableGroup"
              [name]="filteredGroup()!.name"
              (removed)="onFilteredGroupRemoved()"
            />
          }
        </div>
      }
    </div>
  } @else {
    @if (!loading()) {
      <mat-form-field class="custom-form w-full tile-search">
        <app-icon src="/icons/search.svg" matPrefix alt="Search" class="w-4 h-4" />
        <input
          matInput
          [formControl]="searchControl"
          [placeholder]="'pages.campaignDetails.contents.filters.searchPlaceholder' | translate"
        />
      </mat-form-field>
    } @else {
      <ngx-skeleton-loader
        class="w-full h-[30px]"
        [theme]="{ height: '30px', width: '100%', 'border-radius': '100vw' }"
      />
    }
  }
</div>

<!--
  Mobile briefing timeline: one full-width column in the page scroll. The masonry layout still runs, so its inline
  position/size styles are overridden with !important instead of skipping it, which keeps rotating to landscape working.
-->
<div
  class="max-h-[calc(100vh-400px)] overflow-y-auto -mx-4 px-4 -mt-0.5 pt-0.5"
  [ngClass]="{ 'max-md:max-h-none max-md:overflow-visible max-md:mx-0 max-md:px-0': briefingTimeline() }"
>
  <div
    #masonryContainer
    class="relative"
    [ngClass]="{ 'max-md:!h-auto max-md:flex max-md:flex-col max-md:gap-3': briefingTimeline() }"
  >
    @if (!showInitialSkeleton()) {
      @for (tile of visibleTiles(); track tile.id) {
        <div
          #masonryItem
          class="absolute opacity-0"
          [ngClass]="{ 'max-md:!static max-md:!w-full max-md:!h-auto max-md:!opacity-100': briefingTimeline() }"
          [style.width.px]="248"
        >
          <app-social-content-tile
            class="w-full"
            data-testid="social-content-tile"
            [class.max-h-[700px]]="!briefingTimeline()"
            [tileConfiguration]="tile"
            [creatorBriefingMode]="creatorBriefingMode()"
            [briefingTimeline]="briefingTimeline()"
            [exportMode]="exportMode()"
            [campaignExport]="campaignExport()"
            [noCampaignContextMode]="noCampaignContextMode()"
            [clickable]="!exportMode() || campaignExport()?.detailLevel !== EDetailLevel.generalInfo"
            (tileClicked)="tileClicked.emit($event)"
          />
        </div>
      }
    } @else {
      @for (i of [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]; track i) {
        <div
          #masonryItem
          class="absolute opacity-0 h-fit"
          [ngClass]="{ 'max-md:!static max-md:!w-full max-md:!h-auto max-md:!opacity-100': briefingTimeline() }"
          [style.width.px]="248"
        >
          @let height = randomTileHeights[i - 1];

          <ngx-skeleton-loader
            class="flex"
            [style.height.px]="height"
            [theme]="{ width: '100%', height: height + 'px', 'border-radius': '16px' }"
          />
        </div>
      }
    }
  </div>

  @if (!showInitialSkeleton()) {
    <div #sentinel class="h-2 w-full"></div>
  }

  @if (hasMore() && !isAutoLoading()) {
    <div class="w-full flex justify-center items-center py-4">
      <app-dots-spinner />
    </div>
  }
</div>
